/**
 * My Pipeline's ungrouped view — every open quote in the rep's book, sortable.
 *
 * The by-client accordion answers "how is this client's number made up?"; this
 * answers "where is my money, and what is oldest?" A rep chasing the biggest
 * quotes, or working strictly by age, should not have to open six clients to
 * find them. Same levers, same figures, same components — only the grouping
 * differs.
 *
 * Sorting is client-side here purely because this is one rep's book arriving in
 * a single composed response. If this ever grows a page size it becomes a
 * server-side list endpoint and follows `docs/list-endpoints.md` instead.
 */
import { useMemo, useState } from "react";
import { isOverSixMonthsAndWrittenOff } from "@shared-core/domain/my-pipeline-quote-age";
import { ArrowDown, ArrowUp } from "lucide-react";
import type {
  PipelineClient,
  PipelineQuote,
} from "@shared-core/data/my-pipeline-types";
import { Badge } from "../../../primitives/badge";
import { Checkbox } from "../../../primitives/checkbox";
import { QuoteFigures, QuoteLevers, quoteFlagLabel } from "./QuoteLevers";
import type { OnIntervene } from "./QuoteLevers";

type SortKey = "in_month" | "quoted" | "age" | "client" | "conversion";

interface QuoteWithClient {
  quote: PipelineQuote;
  clientName: string;
}

const SORTS: { key: SortKey; label: string; defaultDesc: boolean }[] = [
  { key: "in_month", label: "In month", defaultDesc: true },
  { key: "quoted", label: "Quoted", defaultDesc: true },
  { key: "age", label: "Age", defaultDesc: true },
  { key: "conversion", label: "Conversion", defaultDesc: true },
  { key: "client", label: "Client", defaultDesc: false },
];

export function AllQuotesTable({
  clients,
  flaggedOnly,
  query,
  hideLongDead,
  monthLabel,
  selected,
  focusedQuoteId,
  onToggleSelect,
  onIntervene,
}: {
  clients: PipelineClient[];
  flaggedOnly: boolean;
  query: string;
  /** Hide quotes past their curve AND over six months old. Presentation only. */
  hideLongDead: boolean;
  monthLabel: string;
  selected: Set<string>;
  focusedQuoteId: string | null;
  onToggleSelect: (quoteId: string) => void;
  onIntervene: OnIntervene;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("in_month");
  const [desc, setDesc] = useState(true);

  const rows = useMemo(() => {
    const all: QuoteWithClient[] = clients.flatMap((c) =>
      c.quotes.map((quote) => ({ quote, clientName: c.name })),
    );
    const q = query.trim().toLowerCase();
    const filtered = all
      .filter((r) => !flaggedOnly || r.quote.flags.length > 0)
      .filter((r) => !hideLongDead || !isOverSixMonthsAndWrittenOff(r.quote))
      .filter(
        (r) =>
          !q ||
          r.quote.reference.toLowerCase().includes(q) ||
          r.quote.description.toLowerCase().includes(q) ||
          r.clientName.toLowerCase().includes(q),
      );
    const dir = desc ? -1 : 1;
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case "quoted":
          return dir * (a.quote.quoted_value - b.quote.quoted_value);
        case "age":
          return dir * (a.quote.age_wd - b.quote.age_wd);
        case "conversion":
          return dir * (a.quote.conversion_share - b.quote.conversion_share);
        case "client":
          return dir * a.clientName.localeCompare(b.clientName);
        case "in_month":
        default:
          return dir * (a.quote.weighted_in_month - b.quote.weighted_in_month);
      }
    });
  }, [clients, flaggedOnly, hideLongDead, query, sortKey, desc]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setDesc((d) => !d);
      return;
    }
    setSortKey(key);
    setDesc(SORTS.find((s) => s.key === key)?.defaultDesc ?? true);
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
        {query.trim()
          ? `No quotes match "${query.trim()}".`
          : "Nothing flagged. Every quote in your book has a decision."}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex flex-wrap items-center gap-1 border-b border-border px-3 py-2">
        <span className="mr-1 text-xs text-muted-foreground">Sort</span>
        {SORTS.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => toggleSort(s.key)}
            aria-pressed={sortKey === s.key}
            className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors ${
              sortKey === s.key
                ? "bg-primary/10 font-semibold text-primary"
                : "text-muted-foreground hover:bg-surface-hover"
            }`}
          >
            {s.label}
            {sortKey === s.key &&
              (desc ? (
                <ArrowDown className="size-3" />
              ) : (
                <ArrowUp className="size-3" />
              ))}
          </button>
        ))}
      </div>

      <ul className="divide-y divide-border">
        {rows.map(({ quote, clientName }) => (
          <li
            key={quote.id}
            className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2 ${
              focusedQuoteId === quote.id
                ? "bg-primary/5 ring-1 ring-inset ring-primary/30"
                : ""
            }`}
          >
            <Checkbox
              checked={selected.has(quote.id)}
              onCheckedChange={() => onToggleSelect(quote.id)}
              aria-label={`Select ${quote.reference}`}
              className="shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium">
                  {clientName}
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  {quote.reference}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                <span className="truncate text-xs text-muted-foreground">
                  {quote.description}
                </span>
                {quote.flags.map((f) => (
                  <Badge
                    key={f}
                    variant={f === "written_off" ? "error" : "warning"}
                  >
                    {quoteFlagLabel(f)}
                  </Badge>
                ))}
              </div>
            </div>
            <QuoteFigures
              quote={quote}
              monthLabel={monthLabel}
              onIntervene={onIntervene}
            />
            <QuoteLevers
              quote={quote}
              monthLabel={monthLabel}
              onIntervene={onIntervene}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
